import { NgClass, NgIf, NgTemplateOutlet } from '@angular/common';
import { AfterViewChecked, ChangeDetectorRef, Component, ElementRef, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatCard } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { DynamicFormControlEvent, DynamicFormModel, DynamicTemplateDirective } from '@ng-dynamic-forms/core';
import { DynamicMaterialFormComponent } from '@ng-dynamic-forms/ui-material';
import debounce from 'lodash/debounce';
import { WINDOW } from 'ngx-window-token';
import { CustomDynamicFormService } from '../../custom-dynamic-form.service';
import { FormDefinitionModel } from '../../dynamic-form-components/shared-models/form-definition.model';
import { buildCustomModel } from '../../helpers/build-custom-model.helper';
import { setModelDisabled } from '../../helpers/disable-model.helper';
import { formElementsInvalid } from '../../helpers/form-elements-invalid.helper';
import { ConfirmationDialogComponent, ConfirmationDialogData } from '../confirmation-dialog/confirmation-dialog.component';
import { UploadProgressComponent } from '../upload-progress/upload-progress.component';
import { FormChange, FormSave } from './form.model';

@Component({
  selector:      'gsm-form',
  templateUrl:   './form.component.html',
  styleUrls:     ['./form.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone:    true,
  imports:       [
    NgIf,
    MatIcon,
    MatCard,
    MatIconButton,
    FormsModule,
    ReactiveFormsModule,
    DynamicTemplateDirective,
    NgTemplateOutlet,
    MatButton,
    NgClass,
    MatProgressSpinner,
    UploadProgressComponent,
    DynamicMaterialFormComponent
  ]
})
export class FormComponent implements OnChanges,
                                      OnInit,
                                      AfterViewChecked {
  private el = inject(ElementRef);
  private cd = inject(ChangeDetectorRef);
  private formService = inject(CustomDynamicFormService);
  private dialog = inject(MatDialog);
  private window = inject<Window>(WINDOW);

  formModel: DynamicFormModel;
  formGroup: UntypedFormGroup;
  onChange = debounce(this._onChange, 100);
  hasChanged = false;
  formInvalid = true;
  showUploadProgress = false;

  private _notifyAfterViewCheck = false;
  private _isUploadingTimerId = 0;

  @Input() formDefinitionModel: FormDefinitionModel;
  @Input() isLoading = false;
  @Input() showEditButton = false;
  @Input() isEditable = true;
  @Input() isEditMode = true; // whether to disable form
  @Input() isUploading = false;
  @Input() uploadBytesLoaded = 0;
  @Input() uploadBytesTotal = 0;
  @Input() initLoadingError = false;

  @Output() formChangeEvent = new EventEmitter<FormChange>();
  @Output() customEventEvent = new EventEmitter<CustomEvent>();
  @Output() saveEvent = new EventEmitter<FormSave>();
  @Output() editEvent = new EventEmitter<string>();
  @Output() cancelEvent = new EventEmitter<string>();
  @Output() cancelUploadEvent = new EventEmitter();

  ngOnInit() {
    // not needed as taken care of by ngOnChanges()
    // this.setModels();
  }

  ngOnChanges({
                formDefinitionModel,
                isEditMode,
                isUploading
              }: SimpleChanges) {
    if (formDefinitionModel?.currentValue) {
      if (this.formDefinitionModel.data) {
        this.setModels();

        // set notifyAfterViewCheck flag in order to emit change event after the view is checked
        this._notifyAfterViewCheck = true;
        this.cd.markForCheck();
      }
    }
    if (isEditMode) {
      if (isEditMode.previousValue && !isEditMode.currentValue) {
        // exit edit mode triggered from app -> cancel changes
        this._cancelChanges();
      }
      if (this.formModel) {
        setModelDisabled(this.formModel, !this.isEditMode);
      }
    }

    if (isUploading) {
      // if isUploading changed from false to true
      if (isUploading.currentValue && !isUploading.previousValue) {
        // wait 2 seconds before showing upload progress
        this._isUploadingTimerId = this.window.setTimeout(() => (this.showUploadProgress = true), 2000);
      } else if (!isUploading.currentValue) {
        this.showUploadProgress = false;
        clearTimeout(this._isUploadingTimerId);
      }
    }
  }

  ngAfterViewChecked() {
    // if notifyAfterViewCheck flag is set, reset it and emit change event
    if (this._notifyAfterViewCheck) {
      this._notifyAfterViewCheck = false;
      this._onChange();
      this.hasChanged = false;
    }
  }

  setModels() {
    if (this.formDefinitionModel) {
      if (typeof this.formDefinitionModel === 'string') {
        this.formDefinitionModel = JSON.parse(this.formDefinitionModel);
      }
      // stringify to make sure original data isn't modified
      this.formModel = this.formService.fromJSON(JSON.stringify(this.formDefinitionModel.data));
      this.formGroup = this.formService.createFormGroup(this.formModel);

      if (!this.isEditMode) {
        setModelDisabled(this.formModel, true);
      }
    }
  }

  private _onChange() {
    const customModel = buildCustomModel(this.formModel);
    this.formInvalid = formElementsInvalid(this.formGroup, this.formModel);
    this.hasChanged = true;

    this.formChangeEvent.emit({
      data:        customModel,
      formModel:   this.formModel,
      formInvalid: this.formInvalid
    });
  }

  onCustomEvent(event: DynamicFormControlEvent) {
    if (event.$event instanceof CustomEvent) {
      this.customEventEvent.emit(event.$event);
    }
  }

  onEditClick() {
    this.editEvent.emit(this.formDefinitionModel.taskId);
  }

  onSaveClick() {
    if (this.hasChanged === false) {
      this.onCancelClick();
    } else if (this.formDefinitionModel.changeType === 'decision') {
      this.dialog
        .open<ConfirmationDialogComponent, ConfirmationDialogData>(ConfirmationDialogComponent, {
          autoFocus: true,
          maxWidth:  1000,
          minWidth:  250,
          data:      {
            heading:            'Warnung!',
            content:            'Durch diese Änderungen verwerfen Sie alle bisherigen Eingaben nach diesem Auswahlfeld!',
            confirmButtonLabel: 'Ändern',
            cancelButtonLabel:  'Abbrechen',
            icon:               'warning'
          }
        })
        .afterClosed()
        .subscribe(result => {
          if (result === true) {
            this._saveChanges();
          } else {
            this.onCancelClick();
          }
        });
    } else {
      this._saveChanges();
    }
  }

  onCancelClick() {
    this._cancelChanges();
    this.cancelEvent.emit(this.formDefinitionModel.taskId);
  }

  private _cancelChanges() {
    if (this.hasChanged) {
      this.setModels();
    }
    this.hasChanged = false;
  }

  private async _saveChanges() {
    this.saveEvent.emit({
      formDefinitionModel: {
        ...this.formDefinitionModel,
        data: this.formModel
      },
      data:                buildCustomModel(this.formModel)
    });
  }
}
